import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import PageContainer from '../../components/page-container';
import { useCanRevealPii } from '../../helpers/hooks/pii-reveal-hook';
import {
    SESSION_PAGE_SIZE,
    getSessionTimeline,
    getTraceSessions,
} from '../../services/model/trace-service';
import TurnList from './timeline/turn-list';
import SessionList from './session-list';
import EnvironmentPicker from '../../components/environment-picker';
import { useAppEnvironment } from '../../helpers/hooks/app-environment-hook';

function TracePage() {
    const { appId } = useParams();
    // Test and Live record their traces under different app ids.
    const { env, setEnv, envAppId } = useAppEnvironment(appId);
    // Analytics links straight to an example conversation with
    // ?session=<id>&env=<test|live>.
    const [searchParams] = useSearchParams();
    const linkedSessionId = searchParams.get('session');
    const linkedEnv = searchParams.get('env');

    const [sessions, setSessions] = useState([]);
    const [sessionsLoading, setSessionsLoading] = useState(false);
    const [sessionsNextPage, setSessionsNextPage] = useState(null);
    const [pageSize, setPageSize] = useState(SESSION_PAGE_SIZE);
    // Cursors of the pages already visited, oldest first. The backend cursor
    // only moves forward, so stepping back means re-requesting with the cursor
    // that produced the previous page - index 0 is the first page (no cursor).
    const [cursorStack, setCursorStack] = useState([null]);
    const [search, setSearch] = useState('');

    const [selectedSession, setSelectedSession] = useState(null);
    const [turns, setTurns] = useState([]);
    const [turnsLoading, setTurnsLoading] = useState(false);
    const [turnsUnsupported, setTurnsUnsupported] = useState(false);

    // Personal data is masked. A security admin can switch to the stored values; it is
    // off whenever the page is opened, and the API checks the role on every request.
    const canReveal = useCanRevealPii();
    const [reveal, setReveal] = useState(false);
    // Read through a ref so the fetchers keep their identity: the effect below resets the
    // page whenever fetchSessionPage changes, which would drop the selected session.
    const revealRef = useRef(false);

    /**
     * Load one page of sessions.
     *
     * `cursors` is the new cursor stack: its last entry is the cursor for the
     * page being requested, and its length is the 1-based page number.
     */
    const fetchSessionPage = useCallback(async (cursors, size) => {
        setSessionsLoading(true);
        const requestPage = cursors[cursors.length - 1];
        const page = await getTraceSessions(envAppId, requestPage || undefined, size, revealRef.current);
        setSessions(page.data || []);
        setSessionsNextPage(page.pagination?.nextPage || null);
        setCursorStack(cursors);
        setSessionsLoading(false);
    }, [envAppId]);

    // Refresh reloads the page currently on screen, keeping the user in place.
    const refreshSessions = useCallback(() => {
        return fetchSessionPage(cursorStack, pageSize);
    }, [fetchSessionPage, cursorStack, pageSize]);

    const goToNextPage = useCallback(() => {
        if (!sessionsNextPage || sessionsLoading) {
            return;
        }
        fetchSessionPage([...cursorStack, sessionsNextPage], pageSize);
    }, [fetchSessionPage, cursorStack, sessionsNextPage, sessionsLoading, pageSize]);

    const goToPreviousPage = useCallback(() => {
        if (cursorStack.length <= 1 || sessionsLoading) {
            return;
        }
        fetchSessionPage(cursorStack.slice(0, -1), pageSize);
    }, [fetchSessionPage, cursorStack, sessionsLoading, pageSize]);

    // A different page size invalidates every cursor, so restart from page one.
    const changePageSize = useCallback(size => {
        if (size === pageSize) {
            return;
        }
        setPageSize(size);
        fetchSessionPage([null], size);
    }, [fetchSessionPage, pageSize]);

    const fetchTimeline = useCallback(async sessionId => {
        setTurnsLoading(true);
        const response = await getSessionTimeline(envAppId, sessionId, revealRef.current);
        setTurns(response.data || []);
        setTurnsUnsupported(Boolean(response.unsupported));
        setTurnsLoading(false);
    }, [envAppId]);

    // Reset to the first page whenever the app or environment changes.
    // fetchSessionPage is keyed on the environment's app id, so this also
    // covers the initial load.
    useEffect(() => {
        setSelectedSession(null);
        setTurns([]);
        setSearch('');
        revealRef.current = false;
        setReveal(false);
        fetchSessionPage([null], pageSize);
        // pageSize is intentionally not a dependency: changing it already
        // refetches through changePageSize, and listing it here would fire a
        // second, duplicate request.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchSessionPage]);

    const handleSelectSession = session => {
        setSelectedSession(session);
        setTurns([]);
        fetchTimeline(session.sessionId);
    };

    // Reload what is on screen with the other setting. The ref is updated first because the
    // fetchers read it, and the state only drives the checkbox.
    const handleToggleReveal = checked => {
        revealRef.current = checked;
        setReveal(checked);
        fetchSessionPage(cursorStack, pageSize);
        if (selectedSession) {
            fetchTimeline(selectedSession.sessionId);
        }
    };

    // A link from Analytics names its environment; follow it before loading.
    useEffect(() => {
        if (linkedEnv && linkedEnv !== env) {
            setEnv(linkedEnv);
        }
        // Only when the link changes, not when the user switches afterwards.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkedEnv]);

    // Open the linked session directly; it may not be on the first page of the
    // list, so it is selected by id rather than looked up there.
    useEffect(() => {
        if (linkedSessionId && (!linkedEnv || linkedEnv === env)) {
            setSelectedSession({ sessionId: linkedSessionId });
            fetchTimeline(linkedSessionId);
        }
    }, [linkedSessionId, linkedEnv, env, fetchTimeline]);

    const filteredSessions = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) {
            return sessions;
        }
        return sessions.filter(session => {
            const haystack = [
                session.sessionId,
                session.displayMessage,
                session.handleBy,
                session.refId,
            ].filter(Boolean).join(' ').toLowerCase();
            return haystack.includes(term);
        });
    }, [sessions, search]);

    // There is no total count from a DynamoDB cursor, so the pager shows the
    // row range of the current page rather than "x of y". Pages before this one
    // were full by construction, which makes the offset exact.
    const rangeStart = sessions.length === 0
        ? 0
        : (cursorStack.length - 1) * pageSize + 1;
    const rangeEnd = sessions.length === 0
        ? 0
        : rangeStart + sessions.length - 1;

    return (
        <PageContainer
            title="Trace"
            tooltip="Inspect runtime trace events (spans) recorded for each conversation session"
        >
            <div className="mt-4 flex items-center gap-3">
                <EnvironmentPicker value={env} onChange={setEnv} disabled={sessionsLoading} />
                <span className="text-xs text-gray-500">
                    {env === 'live' ? 'Conversations with the published agent.' : 'Conversations from testing the draft agent.'}
                </span>
                {canReveal && (
                    <label
                        className="ml-auto flex items-center gap-2 text-xs text-gray-700"
                        title="Personal data in traces is masked. Show the stored values (security admins only)."
                    >
                        <input type="checkbox" checked={reveal} onChange={event => handleToggleReveal(event.target.checked)} /> Show raw value
                    </label>
                )}
            </div>
            <div className="mt-4 grid h-[calc(100vh-12rem)] grid-cols-[18rem_minmax(0,1fr)] grid-rows-[minmax(0,1fr)] overflow-hidden rounded-lg border border-gray-200">
                <SessionList
                    sessions={filteredSessions}
                    loading={sessionsLoading}
                    selectedSessionId={selectedSession?.sessionId}
                    onSelect={handleSelectSession}
                    search={search}
                    onSearchChange={setSearch}
                    onRefresh={refreshSessions}
                    pageNumber={cursorStack.length}
                    pageSize={pageSize}
                    onPageSizeChange={changePageSize}
                    rangeStart={rangeStart}
                    rangeEnd={rangeEnd}
                    hasPrevious={cursorStack.length > 1}
                    hasNext={Boolean(sessionsNextPage)}
                    onPrevious={goToPreviousPage}
                    onNext={goToNextPage}
                />

                <div className="flex min-h-0 min-w-0 flex-col bg-white">
                    <div className="min-h-0 flex-1 overflow-y-auto p-4">
                        {!selectedSession ? (
                            <div className="flex h-full items-center justify-center text-center">
                                <div>
                                    <p className="text-sm font-medium text-gray-900">No session selected</p>
                                    <p className="mt-1 text-xs text-gray-500">Pick a session on the left to see what the agent did.</p>
                                </div>
                            </div>
                        ) : turnsLoading ? (
                            <p className="text-xs text-gray-500">Loading&hellip;</p>
                        ) : turnsUnsupported ? (
                            <p className="text-xs text-amber-600">The tracing API does not support the timeline view yet &mdash; deploy the tracing backend.</p>
                        ) : turns.length === 0 ? (
                            <p className="text-xs text-gray-500">This session has no recorded trace.</p>
                        ) : (
                            <TurnList key={selectedSession.sessionId} turns={turns} defaultOpen={turns[turns.length - 1].messageId || `turn-${turns.length - 1}`} />
                        )}
                    </div>
                </div>
            </div>
        </PageContainer>
    );
}

export default TracePage;
